/**
 * Cronos POS Chain Types and Interfaces
 *
 * Type definitions for Cronos POS Chain staking monitoring,
 * delegation tracking, and rewards management.
 * Uses Cosmos SDK standard structures.
 */

// ============================================
// CORE TYPES
// ============================================

/**
 * Cosmos DecCoin structure (amount with denomination)
 */
export interface DecCoin {
  denom: string;
  amount: string;
}

/**
 * Cosmos Coin structure
 */
export interface Coin {
  denom: string;
  amount: string;
}

// ============================================
// VALIDATOR TYPES
// ============================================

/**
 * Validator status enum
 */
export type ValidatorStatus =
  | 'BOND_STATUS_UNSPECIFIED'
  | 'BOND_STATUS_UNBONDED'
  | 'BOND_STATUS_UNBONDING'
  | 'BOND_STATUS_BONDED';

/**
 * Validator description
 */
export interface ValidatorDescription {
  moniker: string;
  identity: string;
  website: string;
  security_contact: string;
  details: string;
}

/**
 * Validator commission rates
 */
export interface ValidatorCommission {
  commission_rates: {
    rate: string;
    max_rate: string;
    max_change_rate: string;
  };
  update_time: string;
}

/**
 * Validator information from Cronos POS Chain
 */
export interface CronosValidator {
  operator_address: string;
  consensus_pubkey: {
    '@type': string;
    key: string;
  };
  jailed: boolean;
  status: ValidatorStatus;
  tokens: string;
  delegator_shares: string;
  description: ValidatorDescription;
  unbonding_height: string;
  unbonding_time: string;
  commission: ValidatorCommission;
  min_self_delegation: string;
}

// ============================================
// DELEGATION TYPES
// ============================================

/**
 * Single delegation entry
 */
export interface CronosDelegation {
  delegation: {
    delegator_address: string;
    validator_address: string;
    shares: string;
  };
  balance: Coin;
}

/**
 * Delegation response from API
 */
export interface CronosDelegationResponse {
  delegation_responses: CronosDelegation[];
  pagination: {
    next_key: string | null;
    total: string;
  };
}

// ============================================
// REWARDS TYPES
// ============================================

/**
 * Delegation reward for a specific validator
 */
export interface CronosDelegationReward {
  validator_address: string;
  reward: DecCoin[];
}

/**
 * All rewards for a delegator
 */
export interface CronosRewardsResponse {
  rewards: CronosDelegationReward[];
  total: DecCoin[];
}

// ============================================
// UNBONDING TYPES
// ============================================

/**
 * Unbonding entry
 */
export interface UnbondingEntry {
  creation_height: string;
  completion_time: string;
  initial_balance: string;
  balance: string;
}

/**
 * Unbonding delegation
 */
export interface CronosUnbondingDelegation {
  delegator_address: string;
  validator_address: string;
  entries: UnbondingEntry[];
}

/**
 * Unbonding delegations response
 */
export interface CronosUnbondingResponse {
  unbonding_responses: CronosUnbondingDelegation[];
  pagination: {
    next_key: string | null;
    total: string;
  };
}

// ============================================
// BALANCE TYPES
// ============================================

/**
 * Account balance response
 */
export interface CronosBalanceResponse {
  balances: Coin[];
  pagination: {
    next_key: string | null;
    total: string;
  };
}

// ============================================
// STAKING PARAMS
// ============================================

/**
 * Staking module parameters
 */
export interface CronosStakingParams {
  params: {
    unbonding_time: string;
    max_validators: number;
    max_entries: number;
    historical_entries: number;
    bond_denom: string;
  };
}

// ============================================
// DISTRIBUTION PARAMS
// ============================================

/**
 * Distribution module parameters
 */
export interface CronosDistributionParams {
  params: {
    community_tax: string;
    base_proposer_reward: string;
    bonus_proposer_reward: string;
    withdraw_addr_enabled: boolean;
  };
}

// ============================================
// AGGREGATED TYPES (for UI)
// ============================================

/**
 * Validator with enhanced info for UI display
 */
export interface CronosValidatorInfo {
  operatorAddress: string;
  moniker: string;
  identity: string;
  website: string;
  details: string;
  jailed: boolean;
  status: ValidatorStatus;
  tokens: number; // Converted from string
  commission: number; // Percentage
  votingPower: number; // Percentage of network
  uptime?: number; // If available
}

/**
 * User's delegation info for a specific validator
 */
export interface CronosDelegationInfo {
  validatorAddress: string;
  validatorMoniker: string;
  validatorCommission: number;
  delegatedAmount: number; // In CRO
  delegatedAmountUsd: number;
  pendingRewards: number; // In CRO
  pendingRewardsUsd: number;
  shares: string;
}

/**
 * User's unbonding delegation info
 */
export interface CronosUnbondingInfo {
  validatorAddress: string;
  validatorMoniker: string;
  amount: number; // In CRO
  amountUsd: number;
  completionTime: Date;
  remainingDays: number;
}

/**
 * Complete wallet data for a Cronos address
 */
export interface CronosWalletData {
  address: string;
  availableBalance: number; // In CRO
  availableBalanceUsd: number;
  totalStaked: number; // In CRO
  totalStakedUsd: number;
  totalRewards: number; // Pending rewards in CRO
  totalRewardsUsd: number;
  totalUnbonding: number; // In CRO
  totalUnbondingUsd: number;
  totalValue: number; // Available + Staked + Unbonding + Rewards
  totalValueUsd: number;
  delegations: CronosDelegationInfo[];
  unbondings: CronosUnbondingInfo[];
  validatorCount: number;
  estimatedApy: number; // Weighted average APY
  lastUpdated: number;
}

/**
 * Network staking statistics
 */
export interface CronosNetworkStats {
  totalBonded: number; // Total CRO staked network-wide
  totalValidators: number;
  activeValidators: number;
  bondDenom: string;
  unbondingTime: number; // In seconds (28 days = 2419200)
  averageApy: number;
  croPrice: number;
}

/**
 * Aggregated data for multiple wallets
 */
export interface CronosAggregatedData {
  wallets: CronosWalletData[];
  totalAvailable: number;
  totalStaked: number;
  totalRewards: number;
  totalUnbonding: number;
  totalValue: number;
  totalValueUsd: number;
  uniqueValidators: number;
  weightedApy: number;
}

// ============================================
// CONSTANTS
// ============================================

/**
 * CRO denomination info
 */
export const CRO_DENOM = {
  base: 'basecro', // 1 CRO = 10^8 basecro
  display: 'CRO',
  exponent: 8,
};

/**
 * Unbonding period in days
 */
export const UNBONDING_DAYS = 28;

/**
 * Chain information
 */
export const CRONOS_POS_CHAIN = {
  chainId: 'crypto-org-chain-mainnet-1',
  chainName: 'Cronos POS Chain',
  rpc: 'https://rpc.mainnet.cronos-pos.org',
  rest: 'https://rest.mainnet.cronos-pos.org',
  explorerUrl: 'https://cronos-pos.org/explorer',
  addressPrefix: 'cro',
};

// ============================================
// LOTTO TYPES
// ============================================

/**
 * Lottery ticket - 6 numbers between 0-15
 */
export interface LottoTicket {
  id: string;
  numbers: number[]; // 6 numbers, each 0-15
  walletAddress: string;
  stakedAmount: number; // CRO staked to earn this ticket
  createdAt: Date;
}

/**
 * Prize tier configuration
 */
export interface LottoPrizeTier {
  name: 'bronze' | 'silver' | 'gold' | 'jackpot' | 'superJackpot';
  matchCount: number; // Number of matching numbers required (from first)
  poolPercentage: number; // % of prize pool
  icon: string;
  color: string;
}

/**
 * Lottery draw result
 */
export interface LottoDraw {
  id: string;
  drawDate: Date;
  winningNumbers: number[];
  prizePool: number; // Total CRO in pool
  prizePoolUsd: number;
  winners: LottoWinner[];
  totalParticipants: number;
  totalTickets: number;
  rolledOver: boolean; // If jackpot rolled over
}

/**
 * Winner information
 */
export interface LottoWinner {
  walletAddress: string;
  tier: LottoPrizeTier['name'];
  matchedNumbers: number;
  prizeAmount: number; // CRO won
  prizeAmountUsd: number;
  claimed: boolean;
  claimedAt?: Date;
  ticketNumbers: number[];
}

/**
 * User's lottery statistics
 */
export interface LottoUserStats {
  walletAddress: string;
  totalTickets: number;
  stakedWithValidator: number; // CRO staked with our validator
  totalWinnings: number; // Lifetime CRO won
  totalWinningsUsd: number;
  unclaimedWinnings: number;
  unclaimedWinningsUsd: number;
  winHistory: LottoWinner[];
  tickets: LottoTicket[];
}

/**
 * Overall lottery statistics
 */
export interface LottoStats {
  currentPrizePool: number;
  currentPrizePoolUsd: number;
  nextDrawDate: Date;
  totalParticipants: number;
  totalTickets: number;
  totalDistributed: number; // Lifetime CRO distributed
  totalDistributedUsd: number;
  validatorAddress: string;
  validatorMoniker: string;
  minStakePerTicket: number; // CRO required for 1 ticket
  drawHistory: LottoDraw[];
}

/**
 * Lotto configuration constants
 */
export const LOTTO_CONFIG = {
  minStakePerTicket: 400, // 400 CRO = 1 ticket
  numbersPerTicket: 6,
  maxNumber: 15,
  minNumber: 0,
  drawFrequency: 'weekly', // weekly, daily
  validatorAddress: 'crocncl1...', // Our validator address
  validatorMoniker: 'Edouard.ai Lotto',
  prizeTiers: [
    { name: 'bronze', matchCount: 2, poolPercentage: 10, icon: '🥉', color: '#CD7F32' },
    { name: 'silver', matchCount: 3, poolPercentage: 15, icon: '🥈', color: '#C0C0C0' },
    { name: 'gold', matchCount: 4, poolPercentage: 20, icon: '🥇', color: '#FFD700' },
    { name: 'jackpot', matchCount: 5, poolPercentage: 25, icon: '💎', color: '#00D4FF' },
    { name: 'superJackpot', matchCount: 6, poolPercentage: 30, icon: '👑', color: '#FF6B6B' },
  ] as const,
};
